/**
 * RadicalToAtomCalculator - RA Arrow Type
 *
 * Handles arrows from radicals to atoms (particles)
 * Common in radical reactions
 */

import { v3 } from "@alchemiesolns/maths";
import {
  ArrowCalculator,
  type ArrowCalculationConfig,
  type ArrowEndpoints,
} from "./types";
import {
  defaultRadicalPositioner,
  type RadicalPositioner,
} from "../positioning/RadicalPositioner";
import { calculateParticleDirection, blendDirections } from "../core/geometry";
import { ENDPOINT_CONSTANTS } from "../core/constants";

/**
 * Calculator for Radical-to-Atom (RA) arrows
 *
 * Source: Radical
 * Destination: Atom (particle)
 */
export class RadicalToAtomCalculator extends ArrowCalculator {
  private radicalPositioner: RadicalPositioner;

  constructor(
    radicalPositioner: RadicalPositioner = defaultRadicalPositioner
  ) {
    super();
    this.radicalPositioner = radicalPositioner;
  }

  getTypeName(): string {
    return "Radical→Atom (RA)";
  }

  calculate(config: ArrowCalculationConfig): ArrowEndpoints {
    const { source, destination, format } = config;

    // Validate types
    if (source.type !== "radical") {
      throw new Error(
        `RadicalToAtomCalculator expects source type 'radical', got '${source.type}'`
      );
    }
    if (destination.type !== "particle") {
      throw new Error(
        `RadicalToAtomCalculator expects destination type 'particle', got '${destination.type}'`
      );
    }

    let endPos = destination.position;
    const radicalPos = source.position;

    // Handle fish arrow (shortens to midpoint if not adjacent)
    const fishResult = this.handleFishArrow(config, radicalPos, endPos);
    endPos = fishResult.endPos;

    // Step 1: Calculate start direction from radical
    const initialStartDir = this.radicalPositioner.calculateStartDirection(
      source,
      endPos,
      format
    );

    // Step 2: Calculate end direction (toward particle)
    let endDir = calculateParticleDirection(endPos, radicalPos);
    endDir = v3.normalize(v3.addVectors(endDir, initialStartDir));

    // Apply adjacency adjustment
    if (config.isDestAdjacent) {
      const dirRef = v3.normalize(v3.subVectors(radicalPos, endPos));
      endDir = v3.normalize(v3.addVectors(endDir, dirRef));
    }

    // Step 3: Blend start and end directions
    const startDir = blendDirections(
      initialStartDir,
      endDir,
      ENDPOINT_CONSTANTS.DIRECTION_BLEND_WEIGHT
    );

    // Step 4: Calculate start position
    const startPos = this.radicalPositioner.calculateStartPoint(
      source,
      startDir,
      format
    );

    // Re-blend end direction
    endDir = blendDirections(
      endDir,
      initialStartDir,
      ENDPOINT_CONSTANTS.DIRECTION_BLEND_WEIGHT
    );

    // Step 5: Calculate final endpoint
    const p3 = v3.addScaledVector(
      endPos,
      endDir,
      config.isDestAdjacent
        ? fishResult.destOffset
        : ENDPOINT_CONSTANTS.PARTICLE_GENERIC_OFFSET
    );

    return {
      startPos,
      endPos,
      p3,
      startDir,
      endDir,
      isDestAdjacent: config.isDestAdjacent,
    };
  }
}

/**
 * Default singleton instance
 */
export const defaultRadicalToAtomCalculator = new RadicalToAtomCalculator();
